import { MODULE_NAVIGATION } from '@/app/config/navigation';
import type { PermissionSpec } from '@/shared/permissions';

/**
 * The set of destinations the account panel may offer.
 *
 * WHY THIS IS STATIC AND WHY IT IS NOT THE SIDEBAR
 *
 * The panel is a launcher, not a second navigation. If it mirrored the sidebar
 * it would be a worse copy of a thing already on screen. Instead it offers a
 * deliberately small set — the handful an admin reaches for while they are
 * mid-task — and it is derived from the ONE navigation registry rather than
 * hand-written, so a destination that leaves the product cannot survive here as
 * a dead link.
 *
 * `MENU_ITEM_IDS` is the allowlist. It is a list of registry ids, not of
 * labels: a label typed twice is a label that drifts, and the sidebar already
 * owns the canonical wording.
 */
const MENU_ITEM_IDS = ['platform.tenants', 'catalog.products', 'bookings.appointments'] as const;

/**
 * A panel destination as the shell *sources* it, before visibility filtering.
 *
 * Distinct from the panel's render shape (`AccountPanelMenuItem` in
 * `shared/layout/profile-panel`) because the two answer different questions:
 * this one carries the permission gates so the shell can filter, and the render
 * shape carries only what is drawn. Collapsing them would push permission
 * checks into a presentational component.
 */
export type AccountPanelSourceItem = {
  readonly id: string;
  readonly to: string;
  readonly label: string;
  readonly description: string;
  readonly icon?: string;
  readonly permission?: PermissionSpec;
  readonly capability?: string;
  /**
   * Which authority axis carries `permission`.
   *
   * Copied from the registry because the panel filters on the SAME axis the
   * sidebar does. Dropping it would check the platform "Tenants" shortcut
   * against the TENANT axis — so a tenant manager would be offered a
   * destination whose grant they do not hold, and the panel would contradict
   * the very sidebar that hides it.
   */
  readonly permissionScope?: 'tenant' | 'platform';
};

/**
 * Descriptions per destination.
 *
 * Held here rather than pushed into `MODULE_NAVIGATION` because the sidebar's
 * two-line rows are a design decision we may not want, and the registry is
 * shared with consumers that have no room for a second line. When the registry
 * grows descriptions of its own, this map is deleted and the property is read
 * straight off the nav item.
 */
const MENU_DESCRIPTIONS: Record<string, string> = {
  'platform.tenants': 'See who has access, and what they can reach',
  'catalog.products': 'Products, pricing and availability',
  'bookings.appointments': 'The schedule across every location',
};

/** The destinations offered, in registry order. */
export const accountPanelMenuItems: ReadonlyArray<AccountPanelSourceItem> = MODULE_NAVIGATION.filter(
  (item) => (MENU_ITEM_IDS as ReadonlyArray<string>).includes(item.id),
)
  .sort((a, b) => a.order - b.order)
  .map((item) => ({
    id: item.id,
    to: item.to,
    label: item.label,
    description: MENU_DESCRIPTIONS[item.id] ?? '',
    permission: item.permission,
    capability: item.capability,
    permissionScope: item.permissionScope,
  }));

/**
 * The one destination that configures the workspace itself — a different kind
 * of thing from the three above, which is why the panel bands it separately.
 *
 * Only the icon and the description live here. The route and the label are read
 * from the registry at render time (see `accountPanelGroupsFor`), because this
 * is the single row the panel and the sidebar must agree about word-for-word,
 * and two typed copies of a label is exactly how they stop agreeing.
 */
export const ACCOUNT_PANEL_SETTINGS = {
  icon: 'settings',
  description: 'Branding, timezone and defaults for this workspace',
} as const;

/** The registry id whose route, label and permission back "Workspace settings". */
export const WORKSPACE_SETTINGS_NAV_ID = 'settings.workspace';
